// frontend/src/pages/Admin/ManageRealizations.jsx
import React, { useEffect, useState } from "react";
import {
  getRealizations,
  getCategories,
  createRealization,
  updateRealization,
  deleteRealization,
} from "../../services/api";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaProjectDiagram,
  FaImage,
} from "react-icons/fa";

const ManageRealizations = () => {
  const [realizations, setRealizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRealization, setEditingRealization] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    client: "",
    date: new Date().toISOString().split("T")[0],
    technologies: "",
    published: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [realizationsRes, categoriesRes] = await Promise.all([
        getRealizations(),
        getCategories(),
      ]);
      setRealizations(realizationsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("category", formData.category);
      data.append("client", formData.client);
      data.append("date", formData.date);
      data.append(
        "technologies",
        JSON.stringify(
          formData.technologies.split("\n").filter((t) => t.trim())
        )
      );
      data.append("published", formData.published);

      if (imageFile) {
        data.append("image", imageFile);
      }

      if (editingRealization) {
        await updateRealization(editingRealization._id, data);
      } else {
        await createRealization(data);
      }

      fetchData();
      resetForm();
    } catch (error) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (realization) => {
    setEditingRealization(realization);
    setFormData({
      title: realization.title,
      description: realization.description,
      category: realization.category._id,
      client: realization.client || "",
      date: new Date(realization.date).toISOString().split("T")[0],
      technologies: realization.technologies?.join("\n") || "",
      published: realization.published,
    });
    setImagePreview(
      `${process.env.REACT_APP_API_URL || "http://51.68.174.64:5000/api"}${
        realization.image
      }`,
    );
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Êtes-vous sûr de vouloir supprimer cette réalisation ?")
    )
      return;

    try {
      await deleteRealization(id);
      fetchData();
    } catch (error) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      client: "",
      date: new Date().toISOString().split("T")[0],
      technologies: "",
      published: true,
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingRealization(null);
    setShowForm(false);
  };

  return (
    <div className="py-8 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <FaProjectDiagram className="text-green-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-800">
              Gestion des Réalisations
            </h1>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FaPlus />
            Nouvelle Réalisation
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {editingRealization
                ? "Modifier la Réalisation"
                : "Nouvelle Réalisation"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Client
                  </label>
                  <input
                    type="text"
                    value={formData.client}
                    onChange={(e) =>
                      setFormData({ ...formData, client: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                ></textarea>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Technologies utilisées (une par ligne)
                </label>
                <textarea
                  value={formData.technologies}
                  onChange={(e) =>
                    setFormData({ ...formData, technologies: e.target.value })
                  }
                  rows="4"
                  placeholder="React&#10;Node.js&#10;MongoDB&#10;..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Image <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    required={!editingRealization}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mt-2 h-32 w-full object-cover rounded"
                    />
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 mt-8">
                    <input
                      type="checkbox"
                      checked={formData.published}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          published: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-green-600"
                    />
                    <span className="text-gray-700 font-semibold">Publié</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                >
                  {loading ? "Enregistrement..." : "Enregistrer"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {realizations.map((realization) => (
            <div
              key={realization._id}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="relative">
                <img
                  src={`${
                    process.env.REACT_APP_API_URL ||
                    "https://api.seengineering-tn.com"
                  }${realization.image}`}
                  alt={realization.title}
                  className="w-full h-48 object-cover"
                />
                {!realization.published && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                    Non publié
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-lg mb-2">{realization.title}</h3>
                <p className="text-sm text-gray-600 mb-1">
                  {realization.category.name}
                </p>
                {realization.client && (
                  <p className="text-sm text-blue-600 mb-2">
                    Client: {realization.client}
                  </p>
                )}
                <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                  {realization.description}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(realization)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                  >
                    <FaEdit size={14} />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(realization._id)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
                  >
                    <FaTrash size={14} />
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {realizations.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FaImage className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 text-lg">
              Aucune réalisation pour le moment
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageRealizations;
